<template>
  <button
    :class="['base-button', `base-button--${variant}`, `base-button--${size || 'md'}`, { 'base-button--loading': loading, 'base-button--disabled': disabled }]"
    :disabled="disabled || loading"
    :type="type || 'button'"
    @click="$emit('click', $event)"
  >
    <span v-if="loading" class="base-button__spinner"></span>
    <slot v-else></slot>
  </button>
</template>

<script setup lang="ts">
defineProps<{
  variant?: 'primary' | 'ghost' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
}>();

defineEmits<{
  click: [event: MouseEvent];
}>();
</script>

<style scoped>
.base-button {
  border: none;
  border-radius: var(--radius-md);
  padding: 0.65rem 1.2rem;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-base);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  min-width: fit-content;
}

.base-button--primary {
  background: var(--color-primary-gradient);
  color: var(--color-text-light);
}

.base-button--primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--shadow-button-hover);
}

.base-button--ghost {
  border: 1px solid var(--color-border-medium);
  background: transparent;
  color: var(--color-text-primary);
}

.base-button--ghost:hover:not(:disabled) {
  background: var(--color-bg-tertiary);
}

.base-button--secondary {
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-light);
}

.base-button--secondary:hover:not(:disabled) {
  background: var(--color-bg-tertiary);
  border-color: var(--color-border-medium);
}

.base-button--disabled,
.base-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none !important;
}

.base-button--sm {
  padding: 0.4rem 0.8rem;
  font-size: var(--font-size-sm);
}

.base-button--md {
  padding: 0.65rem 1.2rem;
  font-size: var(--font-size-lg);
}

.base-button--lg {
  padding: 0.8rem 1.5rem;
  font-size: var(--font-size-xl);
}

.base-button--loading {
  cursor: wait;
}

.base-button__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

